import React, {useState} from 'react';
import {
	Alert,
	Button,
	Checkbox,
	FlexColumn,
	FlexRow,
	Label,
	Pill,
	PillVariant,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {isCustomerPortalAvailable} from '../shared/util/customerPortalAvailability';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {hasModule} from '../shared/util/ModuleUtil';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../constants';
import ClientContactModalContainer, {CreatedClientContact} from '../admin-tab/clients-page/ClientContactModalContainer';
import Util from '../shared/util/util';
import provisionPortalUserMutation from '../../mutations/provision_portal_user_mutation';
import updatePortalUserMutation from '../../mutations/update_portal_user_mutation';
import invitePortalUserMutation from '../../mutations/invite_portal_user_mutation';
import generatePortalUserInviteLinkMutation from '../../mutations/generate_portal_user_invite_link_mutation';

// Clean, non-null shapes the component's render code works with. The raw Relay
// fragment (below) is deeply nullable + readonly; we normalize into these once at
// the top of the component so the render body needn't null-check every field.
type PortalUser = {
	id: string;
	email: string;
	clientContactId: string | null;
	roleKey: string;
	status: string;
	scopeLevel: string;
	scopeProgrammeIds: string[];
	scopeProjectIds: string[];
};

type ClientContact = {
	id: string;
	name: string;
	email: string;
};

type HierarchyNode = {
	id: string;
	name: string;
	programmeId?: string;
	/** False when the firm has not turned on "Show in client portal" for this project. */
	published: boolean;
};

// The prop shape mirrors the query selection in CompanyViewWorkspace_Query: every
// leaf is nullable and lists are ReadonlyArray, because Relay types every selected
// field as potentially null.
type RawPortalUser = {
	readonly id: string | null;
	readonly email: string | null;
	// Optional, like the hierarchy node's own late additions below: a user provisioned before
	// the contact link existed carries no contact, and the field is only read to filter the
	// picker — nothing here needs a caller to state "no contact" explicitly.
	readonly clientContactId?: string | null;
	readonly roleKey: string | null;
	readonly status: string | null;
	readonly scopeLevel: string | null;
	readonly scopeProgrammeIds: ReadonlyArray<string | null> | null;
	readonly scopeProjectIds: ReadonlyArray<string | null> | null;
};

type RawHierarchyNode = {
	readonly id: string | null;
	readonly name: string | null;
	readonly programmeId?: string | null;
	readonly published?: boolean | null;
};

type RawClientContact = {
	readonly id: string | null;
	readonly firstName: string | null;
	readonly lastName: string | null;
	readonly email: string | null;
};

type ClientData = {
	readonly id: string;
	readonly name: string;
	readonly portalConfig?: {
		readonly portalActive: boolean | null;
	} | null;
	// Relay types list elements as nullable too (ReadonlyArray<{...} | null>).
	readonly portalUsers?: ReadonlyArray<RawPortalUser | null> | null;
	readonly clientPortalHierarchy?: {
		readonly programmes: ReadonlyArray<RawHierarchyNode | null> | null;
		readonly projects: ReadonlyArray<RawHierarchyNode | null> | null;
	} | null;
	readonly clientContacts?: {
		readonly edges: ReadonlyArray<{readonly node: RawClientContact | null} | null> | null;
	} | null;
};

// Header line: how many users there are, with the add action at the far end so it is
// reachable without scrolling past the table.
const HeaderBar = Styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
`;

const Summary = Styled.div`
	${props => props.theme.sys.font.body['2']};
	color: ${props => props.theme.sys.color.text.content.auxiliary};
`;

const TableWrap = Styled.div`
	border: 1px solid ${props => props.theme.sys.color.border.decorative};
	border-radius: 8px;
	background-color: ${props => props.theme.sys.color.background.neutral['1']};
	overflow-x: auto;
`;

const UsersTable = Styled.table`
	width: 100%;
	border-collapse: collapse;
`;

// Column headings are set small, spaced and uppercase against a tinted band so the
// header reads as chrome rather than as a first data row.
const HeadCell = Styled.th`
	text-align: left;
	padding: 12px 18px;
	background-color: ${props => props.theme.sys.color.background.neutral['2']};
	${props => props.theme.sys.font.detail['1']};
	font-weight: 500;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: ${props => props.theme.sys.color.text.content.auxiliary};
	white-space: nowrap;
`;

const BodyCell = Styled.td`
	padding: 12px 18px;
	border-top: 1px solid ${props => props.theme.sys.color.border.decorative};
	vertical-align: middle;
`;

// A pending user's row is dimmed: the account exists but nobody is behind it yet.
const UserRow = Styled.tr<{$pending: boolean}>`
	opacity: ${({$pending}) => ($pending ? 0.72 : 1)};
`;

// The invite form sits in its own bordered panel so it reads as a separate task from
// the table it appears under.
const InviteFormCard = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 18px 20px;
	border: 1px solid ${props => props.theme.sys.color.border.decorative};
	border-radius: 8px;
	background-color: ${props => props.theme.sys.color.background.neutral['1']};
`;

const ContactEmail = Styled.div`
	${props => props.theme.sys.font.subheading['2']};
	color: ${props => props.theme.sys.color.text.content.main};
`;

const nonNull = <T,>(value: T | null): value is T => value != null;

// Role and status both read as tinted chips. The design's amber "invited" has no
// counterpart in the design-system Pill, so a pending invitation borrows the
// informational tint — the neutral one is already carrying "deactivated".
const ROLE_PILL_VARIANT: Record<string, PillVariant> = {
	client_executive: PillVariant.INFORMATION,
	active_collaborator: PillVariant.NEUTRAL,
	billing_contact: PillVariant.SUCCESS,
};

const STATUS_PILL_VARIANT: Record<string, PillVariant> = {
	active: PillVariant.SUCCESS,
	invited: PillVariant.INFORMATION,
	not_invited: PillVariant.NEUTRAL,
	invite_expired: PillVariant.INFORMATION,
	deactivated: PillVariant.NEUTRAL,
};

const nonNullStrings = (values: ReadonlyArray<string | null> | null | undefined): string[] =>
	(values ?? []).filter((value): value is string => value != null);

const toHierarchyNode = (node: RawHierarchyNode): HierarchyNode => ({
	id: node.id ?? '',
	name: node.name ?? '',
	programmeId: node.programmeId ?? undefined,
	published: node.published === true,
});

// A project the firm has not shared with the portal is still offered — they may be
// about to share it — but it is labelled, because a scope made only of unshared
// projects leaves the user with an empty portal and no way to tell why.
const projectCheckboxLabel = (project: HierarchyNode, unpublishedNote: string): string =>
	project.published ? project.name : `${project.name} — ${unpublishedNote}`;

const hasUnpublishedSelection = (projects: HierarchyNode[], selectedIds: string[]): boolean =>
	projects.some(project => !project.published && selectedIds.includes(project.id));

type WorkspaceUsersSectionProps = {
	client: ClientData;
	// The modal's LIVE "Activate workspace" toggle. The section appears as soon as
	// the admin flips it on in-session, rather than waiting for a save + fragment
	// refetch. Falls back to the persisted portalConfig.portalActive when omitted.
	workspaceActive?: boolean;
	// Runs the parent's query again after a new contact is created, so the fresh
	// contact appears in the picker without a page reload.
	refetchClient?: () => void;
};

const CLIENT_ROLES = [
	{key: 'client_executive', labelId: 'client_workspace.users.role.client_executive'},
	{key: 'active_collaborator', labelId: 'client_workspace.users.role.active_collaborator'},
	{key: 'billing_contact', labelId: 'client_workspace.users.role.billing_contact'},
];

// The invite form seeds Role to the least-privileged option (Billing Contact) so a
// silent default cannot hand out approval + financial rights on the most privileged
// role. Firm admins deliberately raise the role when they mean to.
const DEFAULT_INVITE_ROLE = 'billing_contact';

// The role select can only offer the roles above, so a user carrying anything else is
// seeded with the first one: a value with no matching <option> would display as the
// first option while a save sent the unshown value.
const knownRoleKey = (roleKey: string): string =>
	CLIENT_ROLES.some(role => role.key === roleKey) ? roleKey : CLIENT_ROLES[0].key;

const contactDisplayName = (firstName: string, lastName: string): string => {
	const joined = `${firstName} ${lastName}`.trim();
	return joined.length > 0 ? joined : '';
};

const WorkspaceUsersSection = ({client, workspaceActive, refetchClient}: WorkspaceUsersSectionProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const [showInviteForm, setShowInviteForm] = useState(false);
	const [inviteContactId, setInviteContactId] = useState<string>('');
	const [inviteRole, setInviteRole] = useState<string>(DEFAULT_INVITE_ROLE);
	const [creatingContact, setCreatingContact] = useState(false);
	// A contact created from this form, held until the refetched query carries it. Without it
	// the picker has no option matching the new id, so a native select falls back to showing
	// the placeholder and the form reads as "no contact chosen" until the refetch lands.
	const [createdContact, setCreatedContact] = useState<ClientContact | null>(null);
	// null = unchosen. Whole-org and scoped grant very different access, so we
	// require the admin to pick one — no silent default to whole-org.
	const [inviteScopeLevel, setInviteScopeLevel] = useState<'client' | 'scoped' | null>(null);
	const [selectedProgrammes, setSelectedProgrammes] = useState<string[]>([]);
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	// Field-level messages appear only after the admin has tried to submit — no
	// pre-emptive scolding on a form the admin is still filling in.
	const [inviteSubmitAttempted, setInviteSubmitAttempted] = useState(false);
	const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>({});
	// Row-level edit UI state. Only one row can be in edit mode at a time; the
	// form values are seeded from the current user's fields when the row opens
	// (never inferred), so cancelling always reverts to what's on the server.
	const [editingUserId, setEditingUserId] = useState<string | null>(null);
	const [editRole, setEditRole] = useState<string>('client_executive');
	const [editScopeLevel, setEditScopeLevel] = useState<'client' | 'scoped'>('client');
	const [editProgrammes, setEditProgrammes] = useState<string[]>([]);
	const [editProjects, setEditProjects] = useState<string[]>([]);

	// Gate: availability + workspace active. Prefer the live toggle (modal context) so
	// the section appears immediately on activation; fall back to the persisted value.
	const isActive = workspaceActive ?? !!client.portalConfig?.portalActive;
	if (!isCustomerPortalAvailable() || !isActive) {
		return null;
	}

	// Normalize the nullable Relay shape into the non-null types the render uses.
	// Both the lists and their elements are nullable, so drop null entries first.
	const users: PortalUser[] = (client.portalUsers ?? []).filter(nonNull).map(user => ({
		id: user.id ?? '',
		email: user.email ?? '',
		clientContactId: user.clientContactId ?? null,
		roleKey: user.roleKey ?? '',
		status: user.status ?? '',
		scopeLevel: user.scopeLevel ?? 'client',
		scopeProgrammeIds: nonNullStrings(user.scopeProgrammeIds),
		scopeProjectIds: nonNullStrings(user.scopeProjectIds),
	}));

	const queriedContacts: ClientContact[] = (client.clientContacts?.edges ?? [])
		.filter(nonNull)
		.map(edge => edge.node)
		.filter(nonNull)
		.map(node => ({
			id: node.id ?? '',
			name: contactDisplayName(node.firstName ?? '', node.lastName ?? ''),
			email: node.email ?? '',
		}))
		.filter(contact => contact.id !== '');

	// The refetched query supersedes the locally held contact as soon as it carries it.
	const contacts: ClientContact[] =
		createdContact && !queriedContacts.some(contact => contact.id === createdContact.id)
			? [...queriedContacts, createdContact]
			: queriedContacts;

	// A contact that already has a workspace user cannot get a second one — the
	// portal enforces it with a UNIQUE (firm_id, psa_contact_id), so offering
	// it would only produce a 409.
	const linkedContactIds = new Set(users.map(u => u.clientContactId).filter((id): id is string => !!id));
	const selectableContacts = contacts.filter(c => !linkedContactIds.has(c.id));
	const selectedContact = selectableContacts.find(c => c.id === inviteContactId);
	// The Client contacts module is per-account. When it is off the client cannot own contacts, so the
	// picker has nothing to show — say that instead of an empty picker.
	const contactsModuleActive =
		hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENT_CONTACTS_FEATURE) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);
	// "Active" counts only users who have actually signed in and are still enabled;
	// everything still waiting on the person (invited, not yet invited, expired link)
	// counts as pending, so the two numbers describe the work left rather than just
	// splitting the rows in half.
	const activeCount = users.filter(user => user.status === 'active').length;
	const pendingCount = users.filter(user => ['invited', 'not_invited', 'invite_expired'].includes(user.status)).length;

	const hierarchy: {programmes: HierarchyNode[]; projects: HierarchyNode[]} = {
		programmes: (client.clientPortalHierarchy?.programmes ?? []).filter(nonNull).map(toHierarchyNode),
		projects: (client.clientPortalHierarchy?.projects ?? []).filter(nonNull).map(toHierarchyNode),
	};

	const unpublishedNote = formatMessage({id: 'client_workspace.users.invite_form.project_unpublished'});

	const inviteContactUnchosen = inviteContactId === '' || selectedContact === undefined;
	const inviteVisibilityUnchosen = inviteScopeLevel === null;
	// Scoped visibility with nothing ticked is the case that silently creates a
	// user who sees zero projects — always blocked, whether the client has a
	// hierarchy or not.
	const inviteScopeIsEmpty =
		inviteScopeLevel === 'scoped' && selectedProgrammes.length === 0 && selectedProjects.length === 0;
	const inviteScopeHasUnpublished =
		inviteScopeLevel === 'scoped' && hasUnpublishedSelection(hierarchy.projects, selectedProjects);

	const inviteHasError = inviteContactUnchosen || inviteVisibilityUnchosen || inviteScopeIsEmpty;

	const resetInviteForm = () => {
		setShowInviteForm(false);
		setInviteContactId('');
		setInviteRole(DEFAULT_INVITE_ROLE);
		setInviteScopeLevel(null);
		setSelectedProgrammes([]);
		setSelectedProjects([]);
		setInviteSubmitAttempted(false);
	};

	const openCreateContactModal = () => {
		setCreatingContact(true);
	};

	// The admin lands back on the form with the person they just added already chosen. The
	// contact is kept locally as well as refetched, so the choice is usable immediately
	// rather than only once the parent query comes back.
	const handleContactSaved = (created?: CreatedClientContact) => {
		if (created) {
			setCreatedContact({
				id: created.id,
				name: contactDisplayName(created.firstName ?? '', created.lastName ?? ''),
				email: created.email ?? '',
			});
			setInviteContactId(created.id);
		}
		refetchClient?.();
	};

	const handleInvite = () => {
		setInviteSubmitAttempted(true);
		if (inviteHasError) {
			return;
		}
		const input = {
			clientOrgId: client.id,
			clientContactId: inviteContactId,
			email: selectedContact?.email ?? '',
			roleKey: inviteRole,
			clientName: client.name,
			scopeLevel: inviteScopeLevel,
			scopeProgrammeIds: inviteScopeLevel === 'scoped' ? selectedProgrammes : [],
			scopeProjectIds: inviteScopeLevel === 'scoped' ? selectedProjects : [],
		};

		Util.CommitMutation(provisionPortalUserMutation, input, () => {
			resetInviteForm();
		});
	};

	const handleResendInvite = (userId: string) => {
		Util.CommitMutation(invitePortalUserMutation, {
			clientOrgId: client.id,
			userId,
		});
	};

	const handleDeactivate = (userId: string) => {
		// A deactivated row has no Edit button, so an open form for it would be stranded.
		if (editingUserId === userId) {
			setEditingUserId(null);
		}
		Util.CommitMutation(updatePortalUserMutation, {
			clientOrgId: client.id,
			userId,
			deactivated: true,
		});
	};

	const handleReactivate = (userId: string) => {
		Util.CommitMutation(updatePortalUserMutation, {
			clientOrgId: client.id,
			userId,
			deactivated: false,
		});
	};

	const handleEditClick = (user: PortalUser) => {
		setEditingUserId(user.id);
		setEditRole(knownRoleKey(user.roleKey));
		setEditScopeLevel(user.scopeLevel === 'scoped' ? 'scoped' : 'client');
		setEditProgrammes(user.scopeProgrammeIds);
		setEditProjects(user.scopeProjectIds);
	};

	const handleCancelEdit = () => {
		setEditingUserId(null);
	};

	// Saving "specific programmes/projects" with nothing ticked replaces the user's
	// visibility with an empty scope, leaving them able to see nothing — and a client
	// with no programmes or projects has nothing to tick at all. Block the save rather
	// than strip the access silently.
	const editScopeIsEmpty = editScopeLevel === 'scoped' && editProgrammes.length === 0 && editProjects.length === 0;

	// A warning, never a gate: publish state is a separate decision the firm can change
	// at any time, so refusing the save would strand a legitimate grant-now-share-next
	// configuration. Only the empty selection is blocked, because that one can never
	// come right on its own.
	const editScopeHasUnpublished = editScopeLevel === 'scoped' && hasUnpublishedSelection(hierarchy.projects, editProjects);

	const handleSaveEdit = (userId: string) => {
		if (editScopeIsEmpty) {
			return;
		}
		const input = {
			clientOrgId: client.id,
			userId,
			roleKey: editRole,
			scopeLevel: editScopeLevel,
			scopeProgrammeIds: editScopeLevel === 'scoped' ? editProgrammes : [],
			scopeProjectIds: editScopeLevel === 'scoped' ? editProjects : [],
		};
		Util.CommitMutation(updatePortalUserMutation, input, () => {
			setEditingUserId(null);
		});
	};

	const toggleEditProgramme = (progId: string) => {
		setEditProgrammes(prev => (prev.includes(progId) ? prev.filter(id => id !== progId) : [...prev, progId]));
	};

	const toggleEditProject = (projId: string) => {
		setEditProjects(prev => (prev.includes(projId) ? prev.filter(id => id !== projId) : [...prev, projId]));
	};

	const handleGetLoginLink = (userId: string) => {
		Util.CommitMutation(generatePortalUserInviteLinkMutation, {clientOrgId: client.id, userId}, (response: unknown) => {
			const url = (response as {generatePortalUserInviteLink?: {url?: string | null}})?.generatePortalUserInviteLink?.url;
			if (url) {
				setRevealedLinks(prev => ({...prev, [userId]: url}));
			}
		});
	};

	const handleCopyLink = (url: string) => {
		navigator.clipboard.writeText(url);
	};

	const getStatusLabel = (status: string): string => {
		const statusMap: Record<string, string> = {
			invited: formatMessage({id: 'client_workspace.users.status.invited'}),
			active: formatMessage({id: 'client_workspace.users.status.active'}),
			deactivated: formatMessage({id: 'client_workspace.users.status.deactivated'}),
			not_invited: formatMessage({id: 'client_workspace.users.status.not_invited'}),
			invite_expired: formatMessage({id: 'client_workspace.users.status.invite_expired'}),
		};
		return statusMap[status] || status;
	};

	const getRoleLabel = (roleKey: string): string => {
		const role = CLIENT_ROLES.find(r => r.key === roleKey);
		return role ? formatMessage({id: role.labelId}) : roleKey;
	};

	const getVisibilityLabel = (user: PortalUser): string => {
		if (user.scopeLevel === 'client') {
			return formatMessage({id: 'client_workspace.users.visibility.client'});
		}
		const scopedCount = (user.scopeProgrammeIds?.length || 0) + (user.scopeProjectIds?.length || 0);
		return `${scopedCount} ${formatMessage({id: 'client_workspace.users.visibility.scoped'})}`;
	};

	const toggleProgramme = (progId: string) => {
		setSelectedProgrammes(prev => (prev.includes(progId) ? prev.filter(id => id !== progId) : [...prev, progId]));
	};

	const toggleProject = (projId: string) => {
		setSelectedProjects(prev => (prev.includes(projId) ? prev.filter(id => id !== projId) : [...prev, projId]));
	};

	return (
		<FlexColumn gap={'l'} data-cy="workspace-users-section">
			<HeaderBar>
				<Summary data-cy="workspace-users-summary">
					<FormattedMessage
						id="client_workspace.users.summary"
						values={{active: activeCount, pending: pendingCount}}
					/>
				</Summary>
				{contactsModuleActive && !showInviteForm && (
					<Button emphasis="high" onPress={() => setShowInviteForm(true)} data-cy="show-invite-form">
						<FormattedMessage id="client_workspace.users.invite_button" />
					</Button>
				)}
			</HeaderBar>

			{/* Contacts read as an empty list when the module is off, which is indistinguishable
			    from "this client has no contacts". Say which it is rather than rendering an empty
			    picker. The existing users table stays visible so an admin can still revoke access
			    granted before the module was turned off. */}
			{!contactsModuleActive && (
				// The test ids sit on a wrapper because the design-system Alert does not forward them.
				<div data-testid="workspace-contacts-disabled" data-cy="workspace-contacts-disabled">
					<Alert type={Alert.TYPE.WARNING} text={formatMessage({id: 'client_workspace.users.contacts_disabled'})} />
				</div>
			)}

			{/* What the two columns actually control, said once, so nobody has to infer the
			    difference between a role and a visibility scope from the values alone. */}
			<Alert type={Alert.TYPE.INFORMATION} text={formatMessage({id: 'client_workspace.users.role_scope_note'})} />

			{/* Users table */}
			{users.length === 0 ? (
				<Text color={'secondary'}>
					<FormattedMessage id="client_workspace.users.no_users" />
				</Text>
			) : (
				<TableWrap>
					<UsersTable>
						<thead>
							<tr>
								<HeadCell>
									<FormattedMessage id="client_workspace.users.contact" />
								</HeadCell>
								<HeadCell>
									<FormattedMessage id="client_workspace.users.role" />
								</HeadCell>
								<HeadCell>
									<FormattedMessage id="client_workspace.users.visibility" />
								</HeadCell>
								<HeadCell>
									<FormattedMessage id="client_workspace.users.status" />
								</HeadCell>
								<HeadCell>
									<FormattedMessage id="client_workspace.users.actions" />
								</HeadCell>
							</tr>
						</thead>
						<tbody>
							{users.map(user => (
								<React.Fragment key={user.id}>
									<UserRow $pending={user.status !== 'active'}>
										<BodyCell>
											<ContactEmail>{user.email}</ContactEmail>
										</BodyCell>
										<BodyCell>
											<Pill variant={ROLE_PILL_VARIANT[user.roleKey] ?? PillVariant.NEUTRAL}>
												{getRoleLabel(user.roleKey)}
											</Pill>
										</BodyCell>
										<BodyCell>
											<Text>{getVisibilityLabel(user)}</Text>
										</BodyCell>
										<BodyCell>
											<Pill variant={STATUS_PILL_VARIANT[user.status] ?? PillVariant.NEUTRAL}>
												{getStatusLabel(user.status)}
											</Pill>
										</BodyCell>
										<BodyCell>
											<FlexRow gap={'xs'}>
												{(user.status === 'not_invited' || user.status === 'invite_expired') && (
													<Button
														emphasis="low"
														onPress={() => handleResendInvite(user.id)}
														data-cy={`send-invite-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.send_invite'})}
													</Button>
												)}
												{user.status === 'invited' && (
													<Button
														emphasis="low"
														onPress={() => handleResendInvite(user.id)}
														data-cy={`resend-invite-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.resend_invite'})}
													</Button>
												)}
												{user.status !== 'deactivated' && (
													<Button
														emphasis="low"
														onPress={() => handleGetLoginLink(user.id)}
														data-cy={`get-login-link-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.get_login_link'})}
													</Button>
												)}
												{user.status !== 'deactivated' && (
													<Button
														emphasis="low"
														onPress={() => handleEditClick(user)}
														data-cy={`edit-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.edit'})}
													</Button>
												)}
												{user.status !== 'deactivated' ? (
													<Button
														emphasis="low"
														onPress={() => handleDeactivate(user.id)}
														data-cy={`deactivate-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.deactivate'})}
													</Button>
												) : (
													<Button
														emphasis="low"
														onPress={() => handleReactivate(user.id)}
														data-cy={`reactivate-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.reactivate'})}
													</Button>
												)}
											</FlexRow>
										</BodyCell>
									</UserRow>
									{revealedLinks[user.id] && (
										<tr>
											<BodyCell colSpan={5}>
												<FlexColumn gap={'xs'}>
													<Label>
														<FormattedMessage id="client_workspace.users.login_link_label" />
													</Label>
													<FlexRow gap={'s'} alignItems={'center'}>
														<TextField
															value={revealedLinks[user.id]}
															disabled
															width="flex"
															data-cy={`login-link-field-${user.id}`}
															data-private
														/>
														<Button
															emphasis="low"
															onPress={() => handleCopyLink(revealedLinks[user.id])}
														>
															{formatMessage({id: 'client_workspace.users.copy_link'})}
														</Button>
													</FlexRow>
													<Text color={'secondary'}>
														<FormattedMessage id="client_workspace.users.login_link_expiry" />
													</Text>
												</FlexColumn>
											</BodyCell>
										</tr>
									)}
									{editingUserId === user.id && (
										<tr data-cy={`edit-form-${user.id}`}>
											<BodyCell colSpan={5}>
												<FlexColumn gap={'m'}>
													<Label>
														<FormattedMessage id="client_workspace.users.edit_form.title" />
													</Label>

													<FlexColumn gap={'s'}>
														<Label>
															<FormattedMessage id="client_workspace.users.invite_form.role_label" />
														</Label>
														<select
															value={editRole}
															onChange={e => setEditRole(e.target.value)}
															style={{padding: '8px', width: '100%'}}
															data-cy={`edit-role-select-${user.id}`}
														>
															{CLIENT_ROLES.map(role => (
																<option key={role.key} value={role.key}>
																	{formatMessage({id: role.labelId})}
																</option>
															))}
														</select>
													</FlexColumn>

													<FlexColumn gap={'s'}>
														<Label>
															<FormattedMessage id="client_workspace.users.invite_form.visibility_label" />
														</Label>
														<Checkbox
															checked={editScopeLevel === 'client'}
															onChange={() => setEditScopeLevel('client')}
															labelText={formatMessage({
																id: 'client_workspace.users.invite_form.whole_org',
															})}
															data-cy={`edit-scope-whole-org-${user.id}`}
														/>
														<Checkbox
															checked={editScopeLevel === 'scoped'}
															onChange={() => setEditScopeLevel('scoped')}
															labelText={formatMessage({
																id: 'client_workspace.users.invite_form.specific_items',
															})}
															data-cy={`edit-scope-specific-${user.id}`}
														/>
													</FlexColumn>

													{editScopeLevel === 'scoped' && (
														<FlexColumn gap={'s'}>
															{hierarchy.programmes.length > 0 && (
																<FlexColumn gap={'xs'}>
																	<Label>
																		<FormattedMessage id="client_workspace.users.invite_form.programmes_label" />
																	</Label>
																	{hierarchy.programmes.map(prog => (
																		<Checkbox
																			key={prog.id}
																			checked={editProgrammes.includes(prog.id)}
																			onChange={() => toggleEditProgramme(prog.id)}
																			labelText={prog.name}
																		/>
																	))}
																</FlexColumn>
															)}
															{hierarchy.projects.length > 0 && (
																<FlexColumn gap={'xs'}>
																	<Label>
																		<FormattedMessage id="client_workspace.users.invite_form.projects_label" />
																	</Label>
																	{hierarchy.projects.map(proj => (
																		<Checkbox
																			key={proj.id}
																			checked={editProjects.includes(proj.id)}
																			onChange={() => toggleEditProject(proj.id)}
																			labelText={projectCheckboxLabel(
																				proj,
																				unpublishedNote
																			)}
																		/>
																	))}
																</FlexColumn>
															)}
															{editScopeIsEmpty && (
																<Text color={'error'} data-cy={`edit-scope-empty-${user.id}`}>
																	<FormattedMessage id="client_workspace.users.edit_form.scope_required" />
																</Text>
															)}
															{editScopeHasUnpublished && (
																<Text data-cy={`edit-scope-unpublished-${user.id}`}>
																	<FormattedMessage id="client_workspace.users.invite_form.unpublished_selected" />
																</Text>
															)}
														</FlexColumn>
													)}

													<FlexRow gap={'s'}>
														<Button
															emphasis="medium"
															disabled={editScopeIsEmpty}
															onPress={() => handleSaveEdit(user.id)}
															data-cy={`edit-save-${user.id}`}
														>
															<FormattedMessage id="client_workspace.users.edit_form.save" />
														</Button>
														<Button
															emphasis="low"
															onPress={handleCancelEdit}
															data-cy={`edit-cancel-${user.id}`}
														>
															<FormattedMessage id="client_workspace.users.invite_form.cancel" />
														</Button>
													</FlexRow>
												</FlexColumn>
											</BodyCell>
										</tr>
									)}
								</React.Fragment>
							))}
						</tbody>
					</UsersTable>
				</TableWrap>
			)}

			{/* The invite form. Its open/close control lives in the header bar above, so the
			    action stays reachable without scrolling past a long user list. */}
			{contactsModuleActive && showInviteForm && (
				<InviteFormCard>
					<Label>
						<FormattedMessage id="client_workspace.users.invite_form.title" />
					</Label>

					<FlexColumn gap={'xs'}>
						<Label>
							<FormattedMessage id="client_workspace.users.invite_form.contact_label" />
						</Label>
						<select
							value={inviteContactId}
							onChange={e => setInviteContactId(e.target.value)}
							style={{padding: '8px', width: '100%'}}
							data-testid="invite-contact-select"
							data-cy="invite-contact-select"
						>
							<option value="">
								{formatMessage({id: 'client_workspace.users.invite_form.contact_placeholder'})}
							</option>
							{selectableContacts.map(contact => (
								<option key={contact.id} value={contact.id}>
									{contact.name ? `${contact.name} (${contact.email})` : contact.email}
								</option>
							))}
						</select>
						{inviteSubmitAttempted && inviteContactUnchosen && (
							<Text color={'error'} data-cy="invite-contact-required-error">
								<FormattedMessage id="client_workspace.users.invite_form.contact_required" />
							</Text>
						)}
						{selectableContacts.length === 0 && (
							<Text data-cy="invite-no-contacts">
								<FormattedMessage id="client_workspace.users.invite_form.no_contacts" />
							</Text>
						)}
						<Button emphasis="low" onPress={openCreateContactModal} data-cy="invite-add-contact">
							<FormattedMessage id="client_workspace.users.invite_form.add_contact" />
						</Button>
					</FlexColumn>

					<FlexColumn gap={'s'}>
						<Label>
							<FormattedMessage id="client_workspace.users.invite_form.role_label" />
						</Label>
						<select
							value={inviteRole}
							onChange={e => setInviteRole(e.target.value)}
							style={{padding: '8px', width: '100%'}}
							data-cy="invite-role-select"
						>
							{CLIENT_ROLES.map(role => (
								<option key={role.key} value={role.key}>
									{formatMessage({id: role.labelId})}
								</option>
							))}
						</select>
					</FlexColumn>

					<FlexColumn gap={'s'}>
						<Label>
							<FormattedMessage id="client_workspace.users.invite_form.visibility_label" />
						</Label>
						<Checkbox
							checked={inviteScopeLevel === 'client'}
							onChange={() => setInviteScopeLevel('client')}
							labelText={formatMessage({id: 'client_workspace.users.invite_form.whole_org'})}
							data-cy="scope-whole-org"
						/>
						<Checkbox
							checked={inviteScopeLevel === 'scoped'}
							onChange={() => setInviteScopeLevel('scoped')}
							labelText={formatMessage({id: 'client_workspace.users.invite_form.specific_items'})}
							data-cy="scope-specific"
						/>
						{inviteSubmitAttempted && inviteVisibilityUnchosen && (
							<Text color={'error'} data-cy="invite-visibility-required-error">
								<FormattedMessage id="client_workspace.users.invite_form.visibility_required" />
							</Text>
						)}
					</FlexColumn>

					{inviteScopeLevel === 'scoped' && (
						<FlexColumn gap={'s'}>
							{hierarchy.programmes.length > 0 && (
								<FlexColumn gap={'xs'}>
									<Label>
										<FormattedMessage id="client_workspace.users.invite_form.programmes_label" />
									</Label>
									{hierarchy.programmes.map(prog => (
										<Checkbox
											key={prog.id}
											checked={selectedProgrammes.includes(prog.id)}
											onChange={() => toggleProgramme(prog.id)}
											labelText={prog.name}
										/>
									))}
								</FlexColumn>
							)}
							{hierarchy.projects.length > 0 && (
								<FlexColumn gap={'xs'}>
									<Label>
										<FormattedMessage id="client_workspace.users.invite_form.projects_label" />
									</Label>
									{hierarchy.projects.map(proj => (
										<Checkbox
											key={proj.id}
											checked={selectedProjects.includes(proj.id)}
											onChange={() => toggleProject(proj.id)}
											labelText={projectCheckboxLabel(proj, unpublishedNote)}
										/>
									))}
								</FlexColumn>
							)}
							{inviteSubmitAttempted && inviteScopeIsEmpty && (
								<Text color={'error'} data-cy="invite-scope-empty-error">
									<FormattedMessage id="client_workspace.users.invite_form.scope_required" />
								</Text>
							)}
							{inviteScopeHasUnpublished && (
								<Text data-cy="invite-scope-unpublished">
									<FormattedMessage id="client_workspace.users.invite_form.unpublished_selected" />
								</Text>
							)}
						</FlexColumn>
					)}

					<FlexRow gap={'s'}>
						<Button emphasis="medium" onPress={handleInvite} data-cy="submit-invite">
							<FormattedMessage id="client_workspace.users.invite_form.submit" />
						</Button>
						<Button emphasis="low" onPress={resetInviteForm} data-cy="cancel-invite">
							<FormattedMessage id="client_workspace.users.invite_form.cancel" />
						</Button>
					</FlexRow>
				</InviteFormCard>
			)}

			{creatingContact && (
				<ClientContactModalContainer
					mode="contact"
					entryPoint="client-page"
					clientId={client.id}
					preSelectedClientName={client.name}
					onContactSaved={handleContactSaved}
					onClose={() => setCreatingContact(false)}
				/>
			)}
		</FlexColumn>
	);
};

export default WorkspaceUsersSection;
